import { QualityScore } from '../Quality';
import { PreflightTestReport } from 'twilio-video';
import { MediaConnectionBitrateTest } from '@twilio/rtc-diagnostics';
import { LivekitQualityStatus } from '../../../AppStateProvider/AppStateProvider';
export function getSingleQualityScore(
  stat: number | undefined,
  goodThreshold: number,
  suboptimalThreshold: number,
  poorThreshold: number,
  descending: boolean = false
) {
  if (typeof stat === 'undefined') {
    // We ignore values that are missing
    return QualityScore.Excellent;
  }

  if (descending) {
    if (stat > goodThreshold) return QualityScore.Excellent;
    if (stat > suboptimalThreshold) return QualityScore.Good;
    if (stat > poorThreshold) return QualityScore.Suboptimal;
    return QualityScore.Poor;
  }

  if (stat >= poorThreshold) return QualityScore.Poor;
  if (stat >= suboptimalThreshold) return QualityScore.Suboptimal;
  if (stat >= goodThreshold) return QualityScore.Good;
  return QualityScore.Excellent;
}

export const formatNumber = (val: number | undefined) => {
  return val?.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

export function getQualityScore(
  preflightTestReport: PreflightTestReport | null,
  bitrateTestReport: MediaConnectionBitrateTest.Report | null
) {
  const maxBitrate = bitrateTestReport?.values ? Math.max(...bitrateTestReport.values) : 0;

  const latency = {
    average: formatNumber(preflightTestReport?.stats!.rtt!.average),
    max: formatNumber(preflightTestReport?.stats!.rtt!.max),
    qualityScore: getSingleQualityScore(preflightTestReport?.stats!.rtt!.average, 100, 250, 400),
  };

  const jitter = {
    average: formatNumber(preflightTestReport?.stats!.jitter!.average),
    max: formatNumber(preflightTestReport?.stats!.jitter!.max),
    qualityScore: getSingleQualityScore(preflightTestReport?.stats!.jitter!.average, 5, 10, 30),
  };

  const packetLoss = {
    average: formatNumber(preflightTestReport?.stats!.packetLoss!.average),
    max: formatNumber(preflightTestReport?.stats!.packetLoss!.max),
    qualityScore: getSingleQualityScore(preflightTestReport?.stats!.packetLoss!.average, 1, 3, 8),
  };

  const bitrate = {
    average: formatNumber(bitrateTestReport?.averageBitrate!),
    max: formatNumber(maxBitrate),
    qualityScore: getSingleQualityScore(bitrateTestReport?.averageBitrate!, 1000, 500, 150, true),
  };

  const totalQualityScore = Math.min(
    latency.qualityScore,
    jitter.qualityScore,
    packetLoss.qualityScore,
    bitrate.qualityScore
  ) as QualityScore;

  const qualityReport = [
    {
      RTT: latency,
      jitter,
      packetLoss,
      bitrate,
    },
  ];

  return {
    qualityReport,
    totalQualityScore,
  };
}

export function LivekitGetQualityScore(LivekitQualityReport: LivekitQualityStatus | null) {
  const latency = {
    average: formatNumber(Number(LivekitQualityReport?.audio!.RTT!.avg)),
    max: formatNumber(Number(LivekitQualityReport?.audio!.RTT!.max)),
    qualityScore: getSingleQualityScore(LivekitQualityReport?.audio!.RTT!.avg, 100, 250, 400),
  };

  const jitter = {
    average: formatNumber(LivekitQualityReport?.audio!.jitter!),
    max: formatNumber(LivekitQualityReport?.audio!.jitter!),
    qualityScore: getSingleQualityScore(LivekitQualityReport?.audio!.jitter!, 5, 10, 30),
  };

  const packetLoss = {
    average: formatNumber(LivekitQualityReport?.audio!.packetLoss!),
    max: formatNumber(LivekitQualityReport?.audio!.packetLoss!),
    qualityScore: getSingleQualityScore(LivekitQualityReport?.audio!.packetLoss!, 1, 3, 8),
  };

  const bitrate = {
    average: formatNumber(Number(LivekitQualityReport?.audio!.bitrate!.avg)),
    max: formatNumber(Number(LivekitQualityReport?.audio!.bitrate!.max)),
    qualityScore: getSingleQualityScore(Number(LivekitQualityReport?.audio!.bitrate!.avg), 1000, 500, 150, true),
  };

  const videoLatency = {
    average: formatNumber(Number(LivekitQualityReport?.video!.RTT!.avg)),
    max: formatNumber(Number(LivekitQualityReport?.video!.RTT!.max)),
    qualityScore: getSingleQualityScore(LivekitQualityReport?.video!.RTT!.avg, 100, 250, 400),
  };

  const videoJitter = {
    average: formatNumber(LivekitQualityReport?.video!.jitter!),
    max: formatNumber(LivekitQualityReport?.video!.jitter!),
    qualityScore: getSingleQualityScore(LivekitQualityReport?.video!.jitter!, 5, 10, 30),
  };

  const videoPacketLoss = {
    average: formatNumber(LivekitQualityReport?.video!.packetLoss),
    max: formatNumber(LivekitQualityReport?.video!.packetLoss),
    qualityScore: getSingleQualityScore(LivekitQualityReport?.video!.packetLoss, 1, 3, 8),
  };

  const videoBitrate = {
    average: formatNumber(Number(LivekitQualityReport?.video!.bitrate!.avg)),
    max: formatNumber(Number(LivekitQualityReport?.video!.bitrate!.max)),
    qualityScore: getSingleQualityScore(Number(LivekitQualityReport?.video!.bitrate!.avg), 1000, 500, 150, true),
  };

  const totalQualityScore = Math.min(
    latency.qualityScore,
    jitter.qualityScore,
    packetLoss.qualityScore,
    bitrate.qualityScore
  ) as QualityScore;

  const videoTotalQualityScore = Math.min(
    videoLatency.qualityScore,
    videoJitter.qualityScore,
    videoPacketLoss.qualityScore,
    videoBitrate.qualityScore
  ) as QualityScore;

  const audio = {
    title: 'Audio',
    RTT: latency,
    jitter,
    packetLoss,
    bitrate,
    totalQualityScore,
  };
  const video = {
    title: 'Video',
    RTT: videoLatency,
    jitter: videoJitter,
    packetLoss: videoPacketLoss,
    bitrate: videoBitrate,
    videoTotalQualityScore,
  };
  const qualityReport = [audio, video];

  return {
    qualityReport,
    totalQualityScore: Math.min(audio.totalQualityScore, video.videoTotalQualityScore) as QualityScore,
  };
}
